// <WhepPlayer> — Ring App Store WHEP live view.
//
// Implementation notes:
//  - Uses `RTCPeerConnection` with a single video transceiver (recvonly) and
//    an optional audio transceiver (recvonly).
//  - On open, builds an SDP offer, POSTs it to the BFF, applies the SDP
//    answer.
//  - Auto-rotation: schedules a pre-open of the next session
//    (session_ttl_seconds - 5) seconds in. On switch, the new MediaStream
//    replaces the current one for a near-seamless cut.
//  - Visibility: tears down after 10s in background; reconnects on visible.
//  - Unload: best-effort DELETE via keepalive fetch.
//
// Replaces the LLD-07 Signalling-based player wherever the Cameras and
// CameraDetail surfaces wired the legacy mock. Consumed from
// CameraDetail (single primary) and Cameras (per-tile, lazy-mount).

import { useCallback, useEffect, useRef, useState } from "react";
import { liveViewClient } from "@/api/liveViewClient";
import "./whep-player.css";

type ConnectionState =
  | "idle"
  | "connecting"
  | "streaming"
  | "reconnecting"
  | "failed"
  | "expired";

interface ActiveSession {
  pc: RTCPeerConnection;
  sessionId: string;
  /**
   * Full BFF teardown URL from the open response Location header. Carries
   * the AVA `?location=...` query param verbatim — pass it through as-is
   * on close, AVA rejects DELETE without it.
   */
  teardownUrl: string;
  /** Wall-clock ms when the BFF expects this session to end. */
  expiresAt: number;
}

const PRE_OPEN_LEAD_MS = 5_000;
const VISIBILITY_TEARDOWN_MS = 10_000;

export interface WhepPlayerProps {
  deviceId: string;
  /** Default audio off; user can toggle (recvonly track). */
  defaultAudio?: boolean;
}

export function WhepPlayer({ deviceId, defaultAudio = false }: WhepPlayerProps): JSX.Element {
  const [state, setState] = useState<ConnectionState>("idle");
  const [autoplayBlocked, setAutoplayBlocked] = useState<boolean>(false);
  const [audioOn, setAudioOn] = useState<boolean>(defaultAudio);
  const [error, setError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const activeRef = useRef<ActiveSession | null>(null);
  const rotationTimerRef = useRef<number | null>(null);
  const visibilityTimerRef = useRef<number | null>(null);
  const teardownInFlightRef = useRef<boolean>(false);

  const closeActive = useCallback(async (): Promise<void> => {
    const cur = activeRef.current;
    activeRef.current = null;
    if (rotationTimerRef.current !== null) {
      window.clearTimeout(rotationTimerRef.current);
      rotationTimerRef.current = null;
    }
    if (cur) {
      try {
        cur.pc.close();
      } catch {
        // ignore
      }
      try {
        await liveViewClient.closeSession(cur.teardownUrl);
      } catch {
        // best effort
      }
    }
  }, [deviceId]);

  const openSession = useCallback(async (): Promise<ActiveSession | null> => {
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });
    pc.addTransceiver("video", { direction: "recvonly" });
    if (audioOn) {
      pc.addTransceiver("audio", { direction: "recvonly" });
    }

    const remoteStream = new MediaStream();
    pc.addEventListener("track", (event) => {
      remoteStream.addTrack(event.track);
      if (videoRef.current) {
        videoRef.current.srcObject = remoteStream;
        const playPromise = videoRef.current.play();
        if (playPromise) {
          playPromise.catch(() => {
            setAutoplayBlocked(true);
          });
        }
      }
    });

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    if (!offer.sdp) {
      pc.close();
      throw new Error("Failed to generate SDP offer");
    }

    const resp = await liveViewClient.openSession(deviceId, offer.sdp);
    await pc.setRemoteDescription({ type: "answer", sdp: resp.sdp_answer });

    const expiresAt = Date.now() + resp.session_ttl_seconds * 1000;
    return { pc, sessionId: resp.session_id, teardownUrl: resp.teardown_url, expiresAt };
  }, [deviceId, audioOn]);

  // The rotate / scheduleRotation / start trio reference each other; use
  // refs to break the cycle so the React `useCallback` graph is stable
  // and react-hooks/exhaustive-deps doesn't churn.
  const rotateRef = useRef<() => Promise<void>>(async () => {});

  const scheduleRotation = useCallback((active: ActiveSession): void => {
    if (rotationTimerRef.current !== null) {
      window.clearTimeout(rotationTimerRef.current);
    }
    const lead = active.expiresAt - Date.now() - PRE_OPEN_LEAD_MS;
    const delay = Math.max(lead, 1000);
    rotationTimerRef.current = window.setTimeout(() => {
      void rotateRef.current();
    }, delay);
  }, []);

  const rotate = useCallback(async (): Promise<void> => {
    setState("reconnecting");
    try {
      const next = await openSession();
      if (!next) return;
      const prev = activeRef.current;
      activeRef.current = next;
      setState("streaming");
      scheduleRotation(next);
      if (prev) {
        try {
          prev.pc.close();
        } catch {
          // ignore
        }
        try {
          await liveViewClient.closeSession(prev.teardownUrl);
        } catch {
          // ignore
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Reconnect failed";
      setError(msg);
      setState("failed");
    }
  }, [deviceId, openSession, scheduleRotation]);

  // Keep the ref pointing at the latest closure so scheduleRotation's timer
  // calls the up-to-date function (avoids stale audioOn captures).
  useEffect(() => {
    rotateRef.current = rotate;
  }, [rotate]);

  const start = useCallback(async (): Promise<void> => {
    setState("connecting");
    setError(null);
    try {
      const next = await openSession();
      if (!next) return;
      activeRef.current = next;
      setState("streaming");
      scheduleRotation(next);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to start live view";
      setError(msg);
      setState("failed");
    }
  }, [openSession, scheduleRotation]);

  // Initial start + cleanup on unmount.
  useEffect(() => {
    void start();
    return () => {
      void closeActive();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  // visibilitychange teardown / restart.
  useEffect(() => {
    function onVisibility(): void {
      if (document.visibilityState === "hidden") {
        if (visibilityTimerRef.current !== null) {
          window.clearTimeout(visibilityTimerRef.current);
        }
        visibilityTimerRef.current = window.setTimeout(() => {
          void closeActive();
          setState("expired");
        }, VISIBILITY_TEARDOWN_MS);
      } else {
        if (visibilityTimerRef.current !== null) {
          window.clearTimeout(visibilityTimerRef.current);
          visibilityTimerRef.current = null;
        }
        if (!activeRef.current) {
          void start();
        }
      }
    }
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [closeActive, start]);

  // beforeunload: DELETE teardown via keepalive fetch.
  useEffect(() => {
    function onBeforeUnload(): void {
      if (teardownInFlightRef.current) return;
      teardownInFlightRef.current = true;
      const cur = activeRef.current;
      if (cur) {
        liveViewClient.closeSessionBeacon(cur.teardownUrl);
      }
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [deviceId]);

  // Audio toggle: rebuild session when toggled.
  const handleToggleAudio = useCallback(async (): Promise<void> => {
    setAudioOn((v) => !v);
    await closeActive();
    void start();
  }, [closeActive, start]);

  function handleAutoplayClick(): void {
    setAutoplayBlocked(false);
    if (videoRef.current) {
      void videoRef.current.play().catch(() => setAutoplayBlocked(true));
    }
  }

  return (
    <div className="reb-whep">
      <div className="reb-whep__video-wrap">
        <video
          ref={videoRef}
          className="reb-whep__video"
          playsInline
          muted={!audioOn}
          autoPlay
        />
        {state === "connecting" && (
          <div className="reb-whep__overlay" role="status">
            Connecting...
          </div>
        )}
        {state === "reconnecting" && (
          <div className="reb-whep__overlay reb-whep__overlay--amber" role="status">
            Reconnecting...
          </div>
        )}
        {state === "expired" && (
          <div className="reb-whep__overlay" role="status">
            Paused. Return to this tab to resume.
          </div>
        )}
        {state === "failed" && (
          <div className="reb-whep__overlay reb-whep__overlay--red" role="alert">
            <div>{error ?? "Live view failed"}</div>
            <button
              type="button"
              className="reb-button"
              onClick={() => void start()}
            >
              Retry
            </button>
          </div>
        )}
        {autoplayBlocked && state === "streaming" && (
          <button
            type="button"
            className="reb-whep__overlay reb-whep__overlay--clickable"
            onClick={handleAutoplayClick}
          >
            Click to start live view
          </button>
        )}
      </div>

      <div className="reb-whep__controls">
        <span
          className={`reb-whep__state reb-whep__state--${state}`}
          aria-live="polite"
        >
          {state === "streaming" ? "Live" : state}
        </span>
        <label className="reb-whep__audio-toggle">
          <input type="checkbox" checked={audioOn} onChange={handleToggleAudio} />
          Audio
        </label>
      </div>
    </div>
  );
}
